/**
 * Decision helpers for the GA4 `purchase` conversion (trial → paid).
 *
 * Kept free of `window` / Vite env so once-only + real-`sub_` rules can be
 * unit-tested without a browser. `analytics.ts` is the only emitter.
 *
 * Authoritative paid signal: a real Paddle subscription id (`sub_…`).
 * `subscription.create` writes placeholder `"temp"` until the webhook
 * (`subscription.created` / `updated`) persists the real id — do not fire on that.
 */

import {
    type AnalyticsEmitDeps,
    type AnalyticsStorage,
    emitPlannedEventSafely,
    normalizeAnalyticsEmail,
    planGtmOrGtagEmission,
    readStorageItem,
    removeStorageItem,
    writeStorageItem,
} from "./analyticsSafe";

export const PURCHASE_EVENT = "purchase";
export const DEFAULT_PURCHASE_CURRENCY = "EUR";
export const PURCHASE_FIRED_STORAGE_KEY = "orsane.purchase_fired";
export const PURCHASE_PENDING_STORAGE_KEY = "orsane.pending_purchase";

export const PAID_CONVERSION_POLL_INTERVAL_MS = 250;
export const PAID_CONVERSION_POLL_ATTEMPTS = 8;
export const PAID_CONVERSION_ATTEMPT_TIMEOUT_MS = 1500;

export type PaidConversionOutcome =
    | "success"
    | "checkout_failed"
    | "placeholder_temp"
    | "missing_subscription_id"
    | "inactive";

export type PaidConversionEventParams = {
    transaction_id?: string;
    value?: number;
    currency: string;
    subscription_id: string;
    plan?: string;
    organization_id?: string;
    quantity?: number;
    user_data?: { email: string };
    ecommerce: {
        transaction_id?: string;
        value?: number;
        currency: string;
        quantity?: number;
        items?: Array<{ item_id?: string; item_name?: string; quantity?: number }>;
    };
};

export type PaidConversionFireDecision =
    | {
          fire: false;
          reason: "not_success" | "already_fired" | "placeholder_temp" | "missing_subscription_id" | "inactive";
      }
    | { fire: true; event: { name: typeof PURCHASE_EVENT; params: PaidConversionEventParams } };

export type PendingPaidConversion = {
    value?: number;
    currency?: string;
    transactionId?: string;
    organizationId?: string;
    quantity?: number;
    plan?: string;
    email?: string;
    checkoutSubscriptionId?: string;
};

export type SubscriptionSnapshot = {
    paddleSubscriptionId?: string | null;
    isActive?: boolean | null;
    status?: string | null;
};

/** Real Paddle subscription ids are `sub_…`. `"temp"` is the client placeholder. */
export function isRealPaddleSubscriptionId(id?: string | null): boolean {
    return typeof id === "string" && id.startsWith("sub_");
}

export function pendingMatchesOrganization(
    pending: PendingPaidConversion | null | undefined,
    organizationId?: string | null
): boolean {
    if (!pending?.organizationId || !organizationId) {
        return true;
    }
    return pending.organizationId === organizationId;
}

export function extractCheckoutSubscriptionId(data: unknown): string | undefined {
    if (!data || typeof data !== "object") {
        return undefined;
    }
    const rec = data as Record<string, unknown>;
    const nested =
        rec.subscription && typeof rec.subscription === "object"
            ? (rec.subscription as { id?: unknown }).id
            : undefined;
    const candidates = [rec.subscription_id, rec.subscriptionId, nested];
    for (const candidate of candidates) {
        if (typeof candidate === "string" && isRealPaddleSubscriptionId(candidate)) {
            return candidate;
        }
    }
    return undefined;
}

export function extractCheckoutValue(
    data: { totals?: { total?: number | string | null } | null } | undefined
): number | undefined {
    const raw = data?.totals?.total;
    const n = typeof raw === "string" ? Number(raw) : raw;
    return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

export function extractCheckoutPlan(data: unknown): string | undefined {
    if (!data || typeof data !== "object") {
        return undefined;
    }
    const rec = data as Record<string, unknown>;
    const items = rec.items;
    if (!Array.isArray(items) || items.length === 0) {
        return undefined;
    }
    const first = items[0] as Record<string, unknown> | undefined;
    if (!first) {
        return undefined;
    }
    if (typeof first.price_id === "string" && first.price_id.length > 0) {
        return first.price_id;
    }
    const price = first.price;
    if (price && typeof price === "object") {
        const priceId = (price as { id?: unknown }).id;
        if (typeof priceId === "string" && priceId.length > 0) {
            return priceId;
        }
    }
    const product = first.product;
    if (product && typeof product === "object") {
        const name = (product as { name?: unknown }).name;
        if (typeof name === "string" && name.length > 0) {
            return name;
        }
    }
    return undefined;
}

export function buildPaidConversionParams(input: {
    paddleSubscriptionId: string;
    value?: number;
    currency?: string;
    transactionId?: string;
    organizationId?: string;
    quantity?: number;
    plan?: string;
    email?: string | null;
}): PaidConversionEventParams {
    const currency = input.currency && input.currency.trim().length > 0 ? input.currency : DEFAULT_PURCHASE_CURRENCY;
    const email = normalizeAnalyticsEmail(input.email);
    const ecommerceItems = input.plan
        ? [{ item_id: input.plan, item_name: input.plan, quantity: input.quantity }]
        : undefined;
    return {
        ...(input.transactionId ? { transaction_id: input.transactionId } : {}),
        ...(typeof input.value === "number" ? { value: input.value } : {}),
        currency,
        subscription_id: input.paddleSubscriptionId,
        ...(input.plan ? { plan: input.plan } : {}),
        ...(input.organizationId ? { organization_id: input.organizationId } : {}),
        ...(typeof input.quantity === "number" ? { quantity: input.quantity } : {}),
        ...(email ? { user_data: { email } } : {}),
        ecommerce: {
            ...(input.transactionId ? { transaction_id: input.transactionId } : {}),
            ...(typeof input.value === "number" ? { value: input.value } : {}),
            currency,
            ...(typeof input.quantity === "number" ? { quantity: input.quantity } : {}),
            ...(ecommerceItems ? { items: ecommerceItems } : {}),
        },
    };
}

export function buildDataLayerPurchaseEvents(params: PaidConversionEventParams): Record<string, unknown>[] {
    return [
        { ecommerce: null },
        {
            event: PURCHASE_EVENT,
            transaction_id: params.transaction_id,
            value: params.value,
            currency: params.currency,
            subscription_id: params.subscription_id,
            ...(params.plan ? { plan: params.plan } : {}),
            ...(params.organization_id ? { organization_id: params.organization_id } : {}),
            ...(typeof params.quantity === "number" ? { quantity: params.quantity } : {}),
            ...(params.user_data ? { user_data: params.user_data } : {}),
            ecommerce: params.ecommerce,
        },
    ];
}

export function planPurchaseEmission(input: {
    gtmId?: string;
    gaMeasurementId?: string;
    params: PaidConversionEventParams;
}): { dataLayerEvents: Record<string, unknown>[]; sendGtag: boolean } {
    return {
        dataLayerEvents: buildDataLayerPurchaseEvents(input.params),
        sendGtag: planGtmOrGtagEmission(input).sendGtag,
    };
}

export function emitPurchaseSafely(
    deps: AnalyticsEmitDeps,
    plan: { dataLayerEvents: Record<string, unknown>[]; sendGtag: boolean },
    params: PaidConversionEventParams
): boolean {
    try {
        for (const event of plan.dataLayerEvents) {
            const ok = emitPlannedEventSafely(deps, { dataLayer: event, sendGtag: false });
            if (!ok) {
                return false;
            }
        }
        if (plan.sendGtag) {
            try {
                if (typeof deps.gtag === "function") {
                    deps.gtag("event", PURCHASE_EVENT, params);
                }
                return true;
            } catch {
                return false;
            }
        }
        return true;
    } catch {
        return false;
    }
}

export function decidePaidConversionFire(input: {
    outcome: PaidConversionOutcome;
    paddleSubscriptionId?: string | null;
    alreadyFired: boolean;
    value?: number;
    currency?: string;
    transactionId?: string;
    organizationId?: string;
    quantity?: number;
    plan?: string;
    email?: string | null;
}): PaidConversionFireDecision {
    if (input.outcome === "checkout_failed") {
        return { fire: false, reason: "not_success" };
    }
    if (input.outcome === "placeholder_temp") {
        return { fire: false, reason: "placeholder_temp" };
    }
    if (input.outcome === "missing_subscription_id") {
        return { fire: false, reason: "missing_subscription_id" };
    }
    if (input.outcome === "inactive") {
        return { fire: false, reason: "inactive" };
    }
    if (input.alreadyFired) {
        return { fire: false, reason: "already_fired" };
    }
    if (!isRealPaddleSubscriptionId(input.paddleSubscriptionId)) {
        if (input.paddleSubscriptionId === "temp") {
            return { fire: false, reason: "placeholder_temp" };
        }
        return { fire: false, reason: "missing_subscription_id" };
    }
    return {
        fire: true,
        event: {
            name: PURCHASE_EVENT,
            params: buildPaidConversionParams({
                paddleSubscriptionId: input.paddleSubscriptionId,
                value: input.value,
                currency: input.currency,
                transactionId: input.transactionId,
                organizationId: input.organizationId,
                quantity: input.quantity,
                plan: input.plan,
                email: input.email,
            }),
        },
    };
}

export function classifyPaidSubscription(snapshot: SubscriptionSnapshot | null | undefined): PaidConversionOutcome {
    const id = snapshot?.paddleSubscriptionId;
    if (!id) {
        return "missing_subscription_id";
    }
    if (id === "temp") {
        return "placeholder_temp";
    }
    if (!isRealPaddleSubscriptionId(id)) {
        return "missing_subscription_id";
    }
    if (snapshot?.isActive === false || (snapshot?.status != null && snapshot.status !== "active")) {
        return "inactive";
    }
    return "success";
}

function defaultSleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), ms);
        promise
            .then((value) => {
                clearTimeout(timer);
                resolve(value);
            })
            .catch(() => {
                clearTimeout(timer);
                resolve(null);
            });
    });
}

/**
 * Resolve a real `sub_…` id from the Paddle overlay (if present) or by polling
 * `subscription.get` until the webhook replaces `"temp"`. Never throws.
 */
export async function resolvePaidSubscriptionId(input: {
    checkoutSubscriptionId?: string | null;
    loadSubscription?: () => Promise<SubscriptionSnapshot | null | undefined>;
    pollAttempts?: number;
    intervalMs?: number;
    attemptTimeoutMs?: number;
    sleep?: (ms: number) => Promise<void>;
}): Promise<SubscriptionSnapshot> {
    try {
        if (isRealPaddleSubscriptionId(input.checkoutSubscriptionId)) {
            return { paddleSubscriptionId: input.checkoutSubscriptionId, isActive: true, status: "active" };
        }
        const load = input.loadSubscription;
        if (!load) {
            return { paddleSubscriptionId: null };
        }
        const attempts = input.pollAttempts ?? 1;
        const intervalMs = input.intervalMs ?? PAID_CONVERSION_POLL_INTERVAL_MS;
        const timeoutMs = input.attemptTimeoutMs ?? PAID_CONVERSION_ATTEMPT_TIMEOUT_MS;
        const sleep = input.sleep ?? defaultSleep;
        for (let i = 0; i < attempts; i++) {
            const snapshot = await withTimeout(
                Promise.resolve().then(() => load()),
                timeoutMs
            );
            if (snapshot && isRealPaddleSubscriptionId(snapshot.paddleSubscriptionId)) {
                return snapshot;
            }
            if (i < attempts - 1) {
                await sleep(intervalMs);
            }
        }
        return { paddleSubscriptionId: null };
    } catch {
        return { paddleSubscriptionId: null };
    }
}

export function parseFiredPaidIds(raw: string | null): string[] {
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
            return parsed.filter((id): id is string => typeof id === "string");
        }
        if (typeof parsed === "string") {
            return [parsed];
        }
    } catch {
        if (raw.startsWith("sub_")) {
            return [raw];
        }
    }
    return [];
}

export function hasFiredPaidConversion(
    storage: AnalyticsStorage | null | undefined,
    paddleSubscriptionId: string
): boolean {
    return parseFiredPaidIds(readStorageItem(storage, PURCHASE_FIRED_STORAGE_KEY)).includes(paddleSubscriptionId);
}

export function markPaidConversionFired(
    storage: AnalyticsStorage | null | undefined,
    paddleSubscriptionId: string
): void {
    const existing = parseFiredPaidIds(readStorageItem(storage, PURCHASE_FIRED_STORAGE_KEY));
    if (existing.includes(paddleSubscriptionId)) {
        return;
    }
    writeStorageItem(storage, PURCHASE_FIRED_STORAGE_KEY, JSON.stringify([...existing, paddleSubscriptionId]));
}

export function markPendingPaidConversion(
    storage: AnalyticsStorage | null | undefined,
    pending: PendingPaidConversion
): void {
    writeStorageItem(storage, PURCHASE_PENDING_STORAGE_KEY, JSON.stringify(pending));
}

export function peekPendingPaidConversion(storage: AnalyticsStorage | null | undefined): PendingPaidConversion | null {
    const raw = readStorageItem(storage, PURCHASE_PENDING_STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as PendingPaidConversion;
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function consumePendingPaidConversion(
    storage: AnalyticsStorage | null | undefined
): PendingPaidConversion | null {
    const pending = peekPendingPaidConversion(storage);
    removeStorageItem(storage, PURCHASE_PENDING_STORAGE_KEY);
    return pending;
}
